import React, { useId, useState } from 'react';
import { Plus, TrendingUp, TrendingDown, Scale } from 'lucide-react';

import Card from './common/Card';
import SafeChart from './common/SafeChart';
import GlowArea from './charts/GlowArea';
import GlowBars from './charts/GlowBars';
import ChartDefs from './charts/ChartDefs';
import useChartTheme from './charts/useChartTheme';
import { makeChartIds } from './charts/chartTokens';

/**
 * NetIncomeTab – tabular & visual breakdown of income minus expenses.
 *
 * Props
 *  ─ netIncomeData: NetIncRow[]  [{ month, income, expenses, netIncome }]
 *  ─ chartsReady:   boolean      – passed straight to <SafeChart>
 *  ─ onUpdateIncomeData: function – callback to update income data
 *  ─ onUpdateExpenseData: function – callback to update expense data
 *  ─ addNextMonth: function – callback to add new month
 */
const NetIncomeTab = ({
  netIncomeData,
  chartsReady,
  onUpdateIncomeData,
  onUpdateExpenseData,
  addNextMonth,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Chart glow system, shared with the tab's charts: the same arcGlow filter + v4
  // tokens power the summary stat badges so they glow in the tab accent (green) and
  // dial the bloom back in light mode automatically.
  const glow = useChartTheme();
  const glowIds = makeChartIds(useId());

  // Format number as currency for display
  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return isNaN(num) ? '' : `$${num.toLocaleString()}`;
  };

  // Parse currency string to number
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  };

  // Handle cell editing
  const handleCellClick = (rowIndex, field) => {
    if (field === 'month' || field === 'netIncome') return; // Don't allow editing month or calculated field

    setEditingCell({ rowIndex, field });
    const currentValue = netIncomeData[rowIndex][field];
    setEditValue(currentValue.toString());
  };

  const handleCellChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleCellBlur = () => {
    if (editingCell) {
      const { rowIndex, field } = editingCell;
      const numericValue = parseCurrency(editValue);

      // Update the appropriate data through parent callbacks
      if (field === 'income') {
        onUpdateIncomeData?.(rowIndex, numericValue);
      } else if (field === 'expenses') {
        onUpdateExpenseData?.(rowIndex, numericValue);
      }
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleCellKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCellBlur();
    }
    if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  const renderEditableCell = (value, rowIndex, field) => {
    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === field;

    if (isEditing) {
      return (
        <input
          type="text"
          value={editValue}
          onChange={handleCellChange}
          onBlur={handleCellBlur}
          onKeyDown={handleCellKeyPress}
          className="w-full px-2 py-1 rounded-arc-sm bg-arc-paper-2 ring-1 ring-arc-line text-arc-ink tnum text-right focus:outline-none focus:ring-2 focus:ring-arc-accent"
          autoFocus
        />
      );
    }

    return (
      <span
        onClick={() => handleCellClick(rowIndex, field)}
        className="cursor-pointer hover:bg-arc-paper px-2 py-1 rounded-arc-sm transition-colors"
      >
        {formatCurrency(value)}
      </span>
    );
  };

  // Summary totals (unchanged math).
  const totalIncome = netIncomeData.reduce((sum, row) => sum + row.income, 0);
  const totalExpenses = netIncomeData.reduce((sum, row) => sum + row.expenses, 0);
  const netTotal = netIncomeData.reduce((sum, row) => sum + row.netIncome, 0);

  // Glowing stat badge: icon chip + hero number, both carrying the arcGlow filter in
  // the given tone (green = positive/accent, red = negative). Same tokens as the charts.
  const StatBadge = ({ Icon, label, value, tone }) => {
    const color = glow.tone[tone];
    return (
      <Card hover={false}>
        <div className="flex items-center gap-2 mb-3">
          <span
            className="inline-flex items-center justify-center w-8 h-8 rounded-arc-sm"
            style={{
              color,
              background: 'var(--arc-accent-soft)',
              filter: `url(#${glowIds.glow})`,
            }}
          >
            <Icon size={16} strokeWidth={2} style={{ color }} />
          </span>
          <h4 className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4">
            {label}
          </h4>
        </div>
        <p
          className="text-hero-num tnum font-bold"
          style={{ color, filter: `url(#${glowIds.glow})` }}
        >
          {formatCurrency(value)}
        </p>
      </Card>
    );
  };

  return (
    <Card hover={false} className="space-y-6">
      {/* Shared glow defs (arcGlow filter + tone gradients) for the stat badges. */}
      <ChartDefs ids={glowIds} colors={glow} />

      <div className="flex justify-between items-center">
        <h3 className="text-h2 text-arc-ink">Net Income Analysis</h3>
        <button
          onClick={addNextMonth}
          className="inline-flex items-center gap-1.5 bg-arc-accent text-white font-semibold rounded-arc-ctl px-4 py-2.5 shadow-arc-sm transition hover:brightness-105 active:brightness-95"
        >
          <Plus size={16} strokeWidth={2.5} /> Add Month
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto">
          <thead>
            <tr>
              {['Month', 'Total Income', 'Total Expenses', 'Net Income'].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.03em] text-arc-ink-4 border-b border-arc-line-2 first:text-left last:text-right text-right"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {netIncomeData.map((row, index) => (
              <tr key={`${row.month}-${index}`} className="hover:bg-arc-paper transition-colors">
                <td className="px-3 py-2.5 font-medium text-arc-ink border-b border-arc-line">
                  {row.month}
                </td>
                <td className="px-3 py-2.5 text-right tnum text-arc-pos border-b border-arc-line">
                  {renderEditableCell(row.income, index, 'income')}
                </td>
                <td className="px-3 py-2.5 text-right tnum text-arc-neg border-b border-arc-line">
                  {renderEditableCell(row.expenses, index, 'expenses')}
                </td>
                <td
                  className={`px-3 py-2.5 text-right tnum font-semibold border-b border-arc-line ${
                    row.netIncome >= 0 ? 'text-arc-pos' : 'text-arc-neg'
                  }`}
                >
                  {formatCurrency(row.netIncome)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Stats — glowing badges in the tab accent (green) / red for negative. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatBadge Icon={TrendingUp} label="Total Income" value={totalIncome} tone="pos" />
        <StatBadge Icon={TrendingDown} label="Total Expenses" value={totalExpenses} tone="neg" />
        <StatBadge
          Icon={Scale}
          label="Net Total"
          value={netTotal}
          tone={netTotal >= 0 ? 'pos' : 'neg'}
        />
      </div>

      {/* Net Income by Month — the signature chart: glowing green bars, red for any
          negative month. */}
      <div>
        <h4 className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4 mb-3">
          Net Income by Month
        </h4>
        <SafeChart chartsReady={chartsReady}>
          <GlowBars
            data={netIncomeData}
            dataKey="netIncome"
            xKey="month"
            height={360}
            yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
            cellTone={(entry) => (entry.netIncome >= 0 ? 'pos' : 'neg')}
          />
        </SafeChart>
      </div>

      {/* Income vs Expenses — glowing green / red area trend. */}
      <div>
        <h4 className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4 mb-3">
          Income vs Expenses
        </h4>
        <SafeChart chartsReady={chartsReady}>
          <GlowArea
            data={netIncomeData}
            xKey="month"
            series={[
              { key: 'income', name: 'Income', tone: 'pos' },
              { key: 'expenses', name: 'Expenses', tone: 'neg' },
            ]}
            yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
          />
        </SafeChart>
      </div>
    </Card>
  );
};

export default NetIncomeTab;
